import type {
  ComposeRecipient,
  SimpleOutboundClientOption,
  SimpleOutboundPreview,
} from '../grid/api/simpleOutboundApi';
import type { ClientEmailSource } from '../grid/components/dialogs/financeEmailComposeUtils';

export type ClientEmailAddressOption = {
  key: string;
  email: string;
  kind: ClientEmailSource;
};

export type SelectedClientDraft = {
  clientId: number;
  clientName: string;
  financeEmail: string;
  clientEmail: string;
  /** Selected address keys (`finance:email` / `client:email`). */
  selectedAddressKeys: string[];
};

export const splitEmails = (value?: string | null): string[] => {
  if (!value?.trim()) {
    return [];
  }

  const seen = new Set<string>();
  const result: string[] = [];

  for (const part of value.split(/[,;]/)) {
    const email = part.trim().toLowerCase();
    if (!email || seen.has(email)) {
      continue;
    }
    seen.add(email);
    result.push(email);
  }

  return result;
};

export const buildClientAddressOptions = (
  client: Pick<SimpleOutboundClientOption, 'financeEmail' | 'clientEmail'>
): ClientEmailAddressOption[] => {
  const seenEmails = new Set<string>();
  const options: ClientEmailAddressOption[] = [];

  for (const email of splitEmails(client.financeEmail)) {
    if (seenEmails.has(email)) {
      continue;
    }
    seenEmails.add(email);
    options.push({
      key: `finance:${email}`,
      email,
      kind: 'finance',
    });
  }

  for (const email of splitEmails(client.clientEmail)) {
    if (seenEmails.has(email)) {
      continue;
    }
    seenEmails.add(email);
    options.push({
      key: `client:${email}`,
      email,
      kind: 'client',
    });
  }

  return options;
};

const emailFromAddressKey = (key: string): string => {
  const separatorIndex = key.indexOf(':');
  return (separatorIndex >= 0 ? key.slice(separatorIndex + 1) : key).toLowerCase();
};

const resolveAddressKeysToOptions = (
  keys: string[],
  options: ClientEmailAddressOption[]
): ClientEmailAddressOption[] => {
  const optionByKey = new Map(options.map((option) => [option.key, option]));
  const optionByEmail = new Map(options.map((option) => [option.email, option]));
  const resolved: ClientEmailAddressOption[] = [];
  const seenEmails = new Set<string>();

  for (const key of keys) {
    const option = optionByKey.get(key) ?? optionByEmail.get(emailFromAddressKey(key));
    if (!option || seenEmails.has(option.email)) {
      continue;
    }
    seenEmails.add(option.email);
    resolved.push(option);
  }

  return resolved;
};

export const defaultSelectedAddressKeys = (
  client: Pick<SimpleOutboundClientOption, 'financeEmail' | 'clientEmail'>,
  preferred: ClientEmailSource
): string[] => {
  const options = buildClientAddressOptions(client);
  if (options.length === 0) {
    return [];
  }

  const preferredOptions = options.filter((option) => option.kind === preferred);
  if (preferredOptions.length > 0) {
    return preferredOptions.map((option) => option.key);
  }

  const fallback: ClientEmailSource = preferred === 'finance' ? 'client' : 'finance';
  return options.filter((option) => option.kind === fallback).map((option) => option.key);
};

export const createSelectedClientDraft = (
  client: SimpleOutboundClientOption,
  preferred: ClientEmailSource
): SelectedClientDraft => ({
  clientId: client.id,
  clientName: client.name || `Client #${client.id}`,
  financeEmail: (client.financeEmail ?? '').trim(),
  clientEmail: (client.clientEmail ?? '').trim(),
  selectedAddressKeys: defaultSelectedAddressKeys(client, preferred),
});

export const resolveSelectedEmails = (draft: SelectedClientDraft): string[] => {
  const options = buildClientAddressOptions(draft);
  return resolveAddressKeysToOptions(draft.selectedAddressKeys, options).map(
    (option) => option.email
  );
};

export const draftToComposeRecipient = (draft: SelectedClientDraft): ComposeRecipient | null => {
  const emails = resolveSelectedEmails(draft);
  if (emails.length === 0) {
    return null;
  }

  return {
    clientId: draft.clientId,
    clientName: draft.clientName,
    to: emails.join('; '),
    financeEmail: draft.financeEmail,
    clientEmail: draft.clientEmail,
  };
};

export const composeRecipientToSimpleOutboundPreview = (
  recipient: ComposeRecipient
): SimpleOutboundPreview => ({
  clientId: recipient.clientId,
  clientName: recipient.clientName,
  financeEmail: recipient.financeEmail,
  clientEmail: recipient.clientEmail,
  to: recipient.to,
  cc: '',
  subject: '',
  message: '',
});

export const draftsToComposeRecipients = (drafts: SelectedClientDraft[]): ComposeRecipient[] =>
  drafts
    .map(draftToComposeRecipient)
    .filter((recipient): recipient is ComposeRecipient => recipient !== null);

export const countRecipientAddresses = (recipients: ComposeRecipient[]): number =>
  recipients.reduce((total, recipient) => total + splitEmails(recipient.to).length, 0);

export const formatRecipientsSummary = (clientCount: number, addressCount: number): string => {
  if (clientCount <= 0) {
    return '0';
  }

  const clientsLabel = `${clientCount} client${clientCount === 1 ? '' : 's'}`;
  const addressesLabel = `${addressCount} address${addressCount === 1 ? '' : 'es'}`;
  return `${clientsLabel} · ${addressesLabel}`;
};

export type StoredComposeClientDraft = {
  clientId: number;
  selectedAddressKeys: string[];
};

export type StoredComposeClientSelection = {
  preferredSource: ClientEmailSource;
  drafts: StoredComposeClientDraft[];
};

export const toStoredComposeSelection = (
  preferredSource: ClientEmailSource,
  selectedById: Record<number, SelectedClientDraft>
): StoredComposeClientSelection => ({
  preferredSource,
  drafts: Object.values(selectedById).map((draft) => ({
    clientId: draft.clientId,
    selectedAddressKeys: [...draft.selectedAddressKeys],
  })),
});

export const storedDraftsToSelectedById = (
  drafts: StoredComposeClientDraft[]
): Record<number, SelectedClientDraft> => {
  const result: Record<number, SelectedClientDraft> = {};
  for (const draft of drafts) {
    if (!draft || typeof draft.clientId !== 'number' || !Array.isArray(draft.selectedAddressKeys)) {
      continue;
    }
    result[draft.clientId] = {
      clientId: draft.clientId,
      clientName: '',
      financeEmail: '',
      clientEmail: '',
      selectedAddressKeys: draft.selectedAddressKeys.filter(
        (key): key is string => typeof key === 'string'
      ),
    };
  }
  return result;
};

export const normalizeStoredComposeSelection = (
  stored: unknown
): StoredComposeClientSelection | null => {
  if (!stored || typeof stored !== 'object') {
    return null;
  }

  const payload = stored as Partial<StoredComposeClientSelection>;
  if (!Array.isArray(payload.drafts)) {
    return null;
  }

  const preferredSource: ClientEmailSource =
    payload.preferredSource === 'client' ? 'client' : 'finance';

  const drafts: StoredComposeClientDraft[] = payload.drafts
    .map((draft) => {
      if (!draft || typeof draft.clientId !== 'number') {
        return null;
      }
      const selectedAddressKeys = Array.isArray(draft.selectedAddressKeys)
        ? draft.selectedAddressKeys.filter((key): key is string => typeof key === 'string')
        : [];
      return {
        clientId: draft.clientId,
        selectedAddressKeys,
      };
    })
    .filter((draft): draft is StoredComposeClientDraft => draft !== null);

  return { preferredSource, drafts };
};

export const reconcileSelectedDrafts = (
  draftsById: Record<number, SelectedClientDraft>,
  clients: SimpleOutboundClientOption[]
): Record<number, SelectedClientDraft> => {
  const clientsById = new Map(clients.map((client) => [client.id, client]));
  const next: Record<number, SelectedClientDraft> = {};

  for (const draft of Object.values(draftsById)) {
    const client = clientsById.get(draft.clientId);
    if (!client) {
      continue;
    }

    const refreshed: SelectedClientDraft = {
      clientId: client.id,
      clientName: client.name || `Client #${client.id}`,
      financeEmail: (client.financeEmail ?? '').trim(),
      clientEmail: (client.clientEmail ?? '').trim(),
      selectedAddressKeys: draft.selectedAddressKeys,
    };
    const options = buildClientAddressOptions(refreshed);
    next[client.id] = {
      ...refreshed,
      selectedAddressKeys: resolveAddressKeysToOptions(refreshed.selectedAddressKeys, options).map(
        (option) => option.key
      ),
    };
  }

  return next;
};
